import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef } from "react";

const SCROLL_END_THRESHOLD_PX = 2;
const SCROLLBAR_INTERACTION_GUTTER_PX = 20;
const USER_SCROLL_INTENT_TIMEOUT_MS = 1000;
const SCROLL_KEYS = new Set([
  "ArrowUp",
  "ArrowDown",
  "PageUp",
  "PageDown",
  "Home",
  "End",
  " ",
  "Spacebar",
]);

const EDITABLE_SELECTOR = "input, textarea, select";

type Props = {
  follow: boolean;
  onCancel: () => void;
  scrollContainer: HTMLElement | Document | null;
  contentElement: HTMLElement | null;
};

/**
 * Normalizes the two scroll-container shapes used by log output.
 *
 * Classic log output follows the document scroll position, while the modern log
 * UI follows an element-local scroll position. Most scroll calculations need an
 * HTMLElement either way.
 */
const getScrollElement = (scrollContainer: HTMLElement | Document | null): HTMLElement | null => {
  if (!scrollContainer) {
    return null;
  }

  if (scrollContainer instanceof Document) {
    return scrollContainer.scrollingElement as HTMLElement | null;
  }

  return scrollContainer;
};

/**
 * Returns true when the viewport is close enough to the log tail that new
 * output should keep following. The small threshold avoids one-pixel rounding
 * differences causing follow mode to cancel near the bottom.
 */
const isScrolledToEnd = (scrollContainer: HTMLElement | Document | null): boolean => {
  const scrollElement = getScrollElement(scrollContainer);
  if (!scrollElement) {
    return false;
  }

  return (
    scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight <=
    SCROLL_END_THRESHOLD_PX
  );
};

const getScrollTop = (scrollContainer: HTMLElement | Document | null): number | null => {
  return getScrollElement(scrollContainer)?.scrollTop ?? null;
};

/**
 * Excludes typing/navigation inside editable controls from keyboard scroll
 * intent detection. For example, pressing Space in an input should not cancel
 * log following.
 */
const isEditableEventTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  if (target.closest(EDITABLE_SELECTOR)) {
    return true;
  }

  const contentEditableElement = target.closest("[contenteditable]");
  if (!(contentEditableElement instanceof HTMLElement)) {
    return false;
  }

  return contentEditableElement.getAttribute("contenteditable") !== "false";
};

const isEventInsideScrollElement = (event: Event, scrollElement: HTMLElement | null): boolean => {
  if (!scrollElement || !(event.target instanceof Node)) {
    return false;
  }

  return scrollElement.contains(event.target);
};

/**
 * Detects mouse-downs in the vertical scrollbar gutter.
 *
 * Browsers do not expose scrollbar drag events directly, but dragging the thumb
 * should cancel follow immediately and keep the gesture active while the mouse
 * is down. The gutter gives us a practical approximation without treating normal
 * clicks inside the log output as scroll intent.
 */
const isLikelyVerticalScrollbarMouseDown = (
  event: MouseEvent,
  scrollElement: HTMLElement,
): boolean => {
  if (
    event.button !== 0 ||
    event.target !== scrollElement ||
    scrollElement.scrollHeight <= scrollElement.clientHeight
  ) {
    return false;
  }

  const rect = scrollElement.getBoundingClientRect();

  return (
    event.clientX >= rect.right - SCROLLBAR_INTERACTION_GUTTER_PX &&
    event.clientX <= rect.right + SCROLLBAR_INTERACTION_GUTTER_PX &&
    event.clientY >= rect.top &&
    event.clientY <= rect.bottom
  );
};

/**
 * Keeps a log viewport pinned to the tail while `follow` is enabled.
 *
 * The important distinction is user intent vs programmatic movement. Live log
 * output can append, truncate, virtualize, or change height as groups are
 * expanded/collapsed; those content/layout changes can fire scroll events too,
 * but should not stop following. Following is canceled only when a real scroll
 * event can be attributed to recent user scroll intent and the viewport has
 * moved away from the tail.
 *
 * A few edge cases are handled deliberately here:
 *
 * - Auto-scrolls set `isAutoScrolling` so their own scroll events don't cancel
 *   follow.
 * - Wheel/key input at the tail can be a no-op. We briefly mark intent, then
 *   clear it if the viewport didn't actually move. If log content grew during
 *   that frame, we clear the stale intent and follow the new tail immediately.
 * - Scrollbar dragging should feel immediate and can last longer than the
 *   recent-intent timeout, so likely scrollbar mousedown starts an active
 *   gesture and cancels following immediately.
 * - Classic log output scrolls the document, while the modern UI scrolls the log
 *   element itself. Keep that distinction explicit when changing this hook.
 */
export const useScrollFollow = ({ follow, onCancel, contentElement, scrollContainer }: Props) => {
  // Flag to indicate if automatic scrolling is in progress so that we know when to cancel if manually scrolling
  const isAutoScrolling = useRef(false);
  const lastUserScrollIntentAt = useRef(Number.NEGATIVE_INFINITY);
  const isUserScrollGestureActive = useRef(false);
  const hasCancelledForCurrentUserScrollIntent = useRef(false);
  const clearUserScrollIntentFrame = useRef<number | null>(null);
  const noopScrollInputScrollTop = useRef<number | null>(null);
  const scrollEventCount = useRef(0);

  // Keep a synchronous ref of the follow state so ResizeObserver callbacks
  // that fire between a state update and the effect cleanup don't scroll.
  const followRef = useRef(follow);
  followRef.current = follow;

  /**
   * Debounced callback for resetting the auto scroll flag.
   *
   * The small delay prevents automatic scrolling from being canceled prematurely and debouncing (rather than setTimeout)
   * handles quick in succession content updates.
   */
  const finishAutoScroll = useMemo(
    () =>
      debounce(() => {
        isAutoScrolling.current = false;
      }, 100),
    [],
  );

  /**
   * Marks the next scroll events as caused by this hook's own auto-scroll.
   */
  const preventScrollCancellation = useCallback(() => {
    isAutoScrolling.current = true;
    finishAutoScroll();
  }, [finishAutoScroll]);

  /**
   * Cancels any pending frame that would clear stale wheel/key scroll intent.
   * Optionally resets all user-intent state during effect cleanup.
   */
  const cancelClearUserScrollIntentFrame = useCallback((clearIntent = false) => {
    if (clearUserScrollIntentFrame.current !== null) {
      window.cancelAnimationFrame(clearUserScrollIntentFrame.current);
      clearUserScrollIntentFrame.current = null;
    }

    if (clearIntent) {
      noopScrollInputScrollTop.current = null;
      isUserScrollGestureActive.current = false;
      hasCancelledForCurrentUserScrollIntent.current = false;
      lastUserScrollIntentAt.current = Number.NEGATIVE_INFINITY;
    }
  }, []);

  /**
   * Clears all evidence that recent input should be treated as user scrolling.
   */
  const clearUserScrollIntent = useCallback(() => {
    cancelClearUserScrollIntentFrame();
    noopScrollInputScrollTop.current = null;
    isUserScrollGestureActive.current = false;
    hasCancelledForCurrentUserScrollIntent.current = false;
    lastUserScrollIntentAt.current = Number.NEGATIVE_INFINITY;
  }, [cancelClearUserScrollIntentFrame]);

  /**
   * Records that the user recently provided input that can produce scrolling.
   */
  const markUserScrollIntent = useCallback(() => {
    cancelClearUserScrollIntentFrame();
    hasCancelledForCurrentUserScrollIntent.current = false;
    lastUserScrollIntentAt.current = Date.now();
  }, [cancelClearUserScrollIntentFrame]);

  /**
   * Starts a continuous user scroll gesture, such as dragging a scrollbar thumb
   * or touch-scrolling the log output.
   */
  const beginUserScrollGesture = useCallback(() => {
    isUserScrollGestureActive.current = true;
    markUserScrollIntent();
  }, [markUserScrollIntent]);

  /**
   * Ends a continuous user scroll gesture and drops stale intent if the gesture
   * finished back at the log tail.
   */
  const finishUserScrollGesture = useCallback(() => {
    isUserScrollGestureActive.current = false;

    if (isScrolledToEnd(scrollContainer)) {
      clearUserScrollIntent();
    }
  }, [clearUserScrollIntent, scrollContainer]);

  /**
   * Returns whether a scroll event should currently be attributed to the user.
   */
  const hasRecentUserScrollIntent = useCallback(() => {
    if (isUserScrollGestureActive.current) {
      return true;
    }

    return Date.now() - lastUserScrollIntentAt.current <= USER_SCROLL_INTENT_TIMEOUT_MS;
  }, []);

  /**
   * Pins the viewport to the log tail unless following is disabled or the user
   * is actively scrolling away from the tail.
   */
  const scrollToEnd = useCallback(() => {
    if (!followRef.current) {
      return;
    }

    if (hasRecentUserScrollIntent() && !isScrolledToEnd(scrollContainer)) {
      return;
    }

    preventScrollCancellation();

    if (Features.BuildSidebar) {
      // New UI works better by scrolling to the end of the scroll container. Ignoring things like scroll margin offsets etc.
      if (scrollContainer) {
        (scrollContainer as HTMLElement).scrollTop = (scrollContainer as HTMLElement).scrollHeight;
      }
    } else {
      // Classic UI uses scrollIntoView since we're scrolling the entire window.
      contentElement?.scrollIntoView({ block: "end" });
    }
  }, [hasRecentUserScrollIntent, scrollContainer, preventScrollCancellation, contentElement]);

  /**
   * Records wheel/key input that may not move the scroll position.
   *
   * When the user scrolls at the tail, the browser can emit no scroll event or a
   * scroll event with the same `scrollTop`. We clear that stale intent on the
   * next frame so a concurrent log append can still auto-follow instead of being
   * mistaken for a user scroll away from the tail.
   */
  const markUserScrollIntentForNoopScrollInput = useCallback(() => {
    markUserScrollIntent();

    if (!isScrolledToEnd(scrollContainer)) {
      return;
    }

    const scrollEventCountAtIntent = scrollEventCount.current;
    noopScrollInputScrollTop.current = getScrollTop(scrollContainer);
    clearUserScrollIntentFrame.current = window.requestAnimationFrame(() => {
      clearUserScrollIntentFrame.current = null;

      if (
        scrollEventCount.current === scrollEventCountAtIntent ||
        getScrollTop(scrollContainer) === noopScrollInputScrollTop.current
      ) {
        clearUserScrollIntent();

        if (!isScrolledToEnd(scrollContainer)) {
          scrollToEnd();
        }
      }
    });
  }, [clearUserScrollIntent, markUserScrollIntent, scrollContainer, scrollToEnd]);

  /**
   * Treats pointer dragging on the scroll element itself as a scrollbar gesture.
   */
  const markPointerMoveScrollIntent = useCallback(
    (event: PointerEvent) => {
      if (event.buttons > 0 && event.target === getScrollElement(scrollContainer)) {
        beginUserScrollGesture();
      }
    },
    [beginUserScrollGesture, scrollContainer],
  );

  /**
   * Cancels follow immediately when the user starts dragging the vertical
   * scrollbar, before the browser emits the resulting scroll event.
   */
  const markScrollbarMouseIntent = useCallback(
    (event: MouseEvent) => {
      const scrollElement = getScrollElement(scrollContainer);

      if (scrollElement && isLikelyVerticalScrollbarMouseDown(event, scrollElement)) {
        beginUserScrollGesture();
        hasCancelledForCurrentUserScrollIntent.current = true;
        onCancel();
      }
    },
    [beginUserScrollGesture, onCancel, scrollContainer],
  );

  /**
   * Marks keyboard input as scroll intent only when it targets the scrollable log
   * viewport and is not being used to edit text.
   */
  const markKeyboardScrollIntent = useCallback(
    (event: KeyboardEvent) => {
      const scrollElement = getScrollElement(scrollContainer);

      if (
        SCROLL_KEYS.has(event.key) &&
        isEventInsideScrollElement(event, scrollElement) &&
        !isEditableEventTarget(event.target)
      ) {
        markUserScrollIntentForNoopScrollInput();
      }
    },
    [markUserScrollIntentForNoopScrollInput, scrollContainer],
  );

  /**
   * Handles scroll events from both user and programmatic movement.
   *
   * Content changes can emit scroll events while follow mode is keeping the log
   * pinned. Only cancel when a scroll event follows recent user intent and ends
   * away from the log tail; otherwise either ignore our own auto-scroll or
   * repair the position back to the tail.
   */
  const cancelScroll = useCallback(() => {
    scrollEventCount.current += 1;

    const hasUserScrollIntent = hasRecentUserScrollIntent();

    if (
      hasUserScrollIntent &&
      noopScrollInputScrollTop.current !== null &&
      getScrollTop(scrollContainer) === noopScrollInputScrollTop.current
    ) {
      clearUserScrollIntent();
      scrollToEnd();
      return;
    }

    if (isAutoScrolling.current && !hasUserScrollIntent) {
      return;
    }

    if (isScrolledToEnd(scrollContainer)) {
      clearUserScrollIntent();
      return;
    }

    if (hasUserScrollIntent) {
      const hasAlreadyCancelled = hasCancelledForCurrentUserScrollIntent.current;
      clearUserScrollIntent();

      if (!hasAlreadyCancelled) {
        onCancel();
      }
    } else {
      scrollToEnd();
    }
  }, [clearUserScrollIntent, hasRecentUserScrollIntent, onCancel, scrollContainer, scrollToEnd]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      scrollToEnd();
    });

    const scrollElement = getScrollElement(scrollContainer);

    if (follow) {
      if (contentElement && scrollContainer) {
        scrollToEnd();
        scrollContainer?.addEventListener("scroll", cancelScroll);
        scrollElement?.addEventListener("wheel", markUserScrollIntentForNoopScrollInput, {
          passive: true,
        });
        document.addEventListener("mousedown", markScrollbarMouseIntent, {
          capture: true,
          passive: true,
        });
        window.addEventListener("mouseup", finishUserScrollGesture, { passive: true });
        scrollElement?.addEventListener("touchstart", beginUserScrollGesture, { passive: true });
        scrollElement?.addEventListener("touchmove", markUserScrollIntent, { passive: true });
        scrollElement?.addEventListener("touchend", finishUserScrollGesture, {
          passive: true,
        });
        scrollElement?.addEventListener("touchcancel", finishUserScrollGesture, {
          passive: true,
        });
        scrollElement?.addEventListener("pointermove", markPointerMoveScrollIntent, {
          passive: true,
        });
        scrollElement?.addEventListener("pointerup", finishUserScrollGesture, {
          passive: true,
        });
        scrollElement?.addEventListener("pointercancel", finishUserScrollGesture, {
          passive: true,
        });
        document.addEventListener("keydown", markKeyboardScrollIntent);
        contentElement.addEventListener("click", preventScrollCancellation);
        resizeObserver.observe(contentElement);
      }
    }

    return () => {
      scrollContainer?.removeEventListener("scroll", cancelScroll);
      scrollElement?.removeEventListener("wheel", markUserScrollIntentForNoopScrollInput);
      document.removeEventListener("mousedown", markScrollbarMouseIntent, { capture: true });
      window.removeEventListener("mouseup", finishUserScrollGesture);
      scrollElement?.removeEventListener("touchstart", beginUserScrollGesture);
      scrollElement?.removeEventListener("touchmove", markUserScrollIntent);
      scrollElement?.removeEventListener("touchend", finishUserScrollGesture);
      scrollElement?.removeEventListener("touchcancel", finishUserScrollGesture);
      scrollElement?.removeEventListener("pointermove", markPointerMoveScrollIntent);
      scrollElement?.removeEventListener("pointerup", finishUserScrollGesture);
      scrollElement?.removeEventListener("pointercancel", finishUserScrollGesture);
      document.removeEventListener("keydown", markKeyboardScrollIntent);
      contentElement?.removeEventListener("click", preventScrollCancellation);
      resizeObserver.disconnect();
      finishAutoScroll.cancel();
      cancelClearUserScrollIntentFrame(true);
    };
  }, [
    follow,
    scrollContainer,
    contentElement,
    scrollToEnd,
    cancelScroll,
    markUserScrollIntent,
    beginUserScrollGesture,
    finishUserScrollGesture,
    markUserScrollIntentForNoopScrollInput,
    markPointerMoveScrollIntent,
    markScrollbarMouseIntent,
    markKeyboardScrollIntent,
    preventScrollCancellation,
    finishAutoScroll,
    cancelClearUserScrollIntentFrame,
  ]);
};
